import { createSelector } from 'reselect';
import { flow, pipe, constUndefined } from 'fp-ts/function';
import { record, option, array, string } from 'fp-ts';
import { Resource } from '@mews-commander/resources';
import { getStringProp } from '@mews/fp';
import { composeProps, createPropsExtender } from '@mews/framework';
import {
    getResourceCategoryAssignments,
    getResourceIdsByResourceCategoryIds,
} from '@mews-commander/legacy-entities/src/ducks/resourceCategoryAssignments';
import { getResources } from '@mews-commander/legacy-entities/src/ducks/resources';
import { getEnterpriseIdFromServiceId } from '@mews-commander/legacy-entities/src/ducks/services';
import { sortFloors } from '@mews-commander/legacy-entities/src/selectors/spaces';
import { Entities } from '@mews-commander/legacy-entities/src/ducks/utils';
import { FilterValues } from '../types';
import {
    getAvailabilityAdjustmentsUniqResourceCategoryIdsByResourceCategoryType,
    getResourceCategoryIdsOfSelectedResourceCategoryType,
} from './entities';
import { getScreen } from './screens';
import { getReservationRequestedResourceIds } from './reservations';

export const getFilterValues = createSelector(getScreen, (screen) => screen?.filterValues);

export const getSelectedResourceIdFromFilterValues = flow(
    getFilterValues,
    record.lookup('selectedResourceId'),
    option.toNullable
);

export const isAvailabilityDropdownShown = flow(
    getSelectedResourceIdFromFilterValues,
    option.fromNullable,
    option.isNone
);

export const getResourceCategoryIds = createSelector(
    composeProps(getResources, createPropsExtender('enterpriseId', getEnterpriseIdFromServiceId)),
    getResourceCategoryAssignments,
    getFilterValues,
    getResourceCategoryIdsOfSelectedResourceCategoryType,
    getAvailabilityAdjustmentsUniqResourceCategoryIdsByResourceCategoryType,
    getStringProp('serviceId'),
    getStringProp('availabilityBlockId'),
    (
        resources: Entities<Resource>,
        resourceCategoryAssignments,
        filterValues: FilterValues,
        resourceCategoryTypeResourceCategoryIds: string[],
        availabilityBlockResourceCategoryIds,
        serviceId,
        availabilityBlockId
    ) => {
        const { selectedResourceId, selectedFloorNumbers } = filterValues;
        const availableResourceCategoryIds = availabilityBlockId
            ? availabilityBlockResourceCategoryIds
            : resourceCategoryTypeResourceCategoryIds;

        if (array.isEmpty(selectedFloorNumbers)) {
            return [];
        }

        const assignments = pipe(
            resourceCategoryAssignments,
            record.collect(string.Ord)((_, resourceCategoryAssignment) => resourceCategoryAssignment)
        );

        const availableFloorNumbers = pipe(
            availableResourceCategoryIds,
            array.map((id) =>
                pipe(
                    assignments,
                    array.filterMap(({ resourceId, categoryId, serviceId: assignmentServiceId }) =>
                        categoryId === id && assignmentServiceId === serviceId
                            ? pipe(
                                  resources,
                                  record.lookup(resourceId),
                                  option.filterMap(({ data }) => option.fromNullable(data.value.floorNumber))
                              )
                            : option.none
                    )
                )
            ),
            array.flatten,
            array.uniq(string.Eq)
        );

        if (selectedResourceId === null && array.size(selectedFloorNumbers) === array.size(availableFloorNumbers)) {
            return availableResourceCategoryIds;
        }

        if (selectedResourceId !== null) {
            const resourceCategoryId = pipe(
                assignments,
                array.findFirstMap(({ resourceId, categoryId, serviceId: assignmentServiceId }) =>
                    resourceId === selectedResourceId &&
                    assignmentServiceId === serviceId &&
                    availableResourceCategoryIds.includes(categoryId)
                        ? option.some(categoryId)
                        : option.none
                ),
                option.match(constUndefined, (categoryId) => categoryId)
            );
            return resourceCategoryId !== undefined ? [resourceCategoryId] : availableResourceCategoryIds;
        }

        return pipe(
            resources,
            record.reduce(string.Ord)<Resource, string[]>([], (acc, { id, data }) => {
                const { floorNumber } = data.value;
                if (floorNumber !== undefined && selectedFloorNumbers.includes(floorNumber)) {
                    const resourceCategoryId = pipe(
                        assignments,
                        array.findFirstMap(({ resourceId, categoryId, serviceId: assignmentServiceId }) =>
                            resourceId === id &&
                            assignmentServiceId === serviceId &&
                            availableResourceCategoryIds.includes(categoryId)
                                ? option.some(categoryId)
                                : option.none
                        ),
                        option.match(constUndefined, (categoryId) => categoryId)
                    );

                    if (resourceCategoryId !== undefined) {
                        return [...acc, resourceCategoryId];
                    }
                }
                return acc;
            }),
            array.uniq(string.Eq)
        );
    }
);

const getExcludedResourceIdsBySelectedFloorNumbers = createSelector(
    composeProps(getResources, createPropsExtender('enterpriseId', getEnterpriseIdFromServiceId)),
    getFilterValues,
    (resources, { selectedFloorNumbers }: FilterValues) =>
        pipe(
            resources,
            record.collect(string.Ord)((_, resource: Resource) => resource),
            array.filterMap(({ id, data }) =>
                selectedFloorNumbers.includes(data.value.floorNumber) ? option.none : option.some(id)
            )
        )
);

export const getExcludedResourceIds = createSelector(
    getExcludedResourceIdsBySelectedFloorNumbers,
    getReservationRequestedResourceIds,
    getSelectedResourceIdFromFilterValues,
    (excludedResourceIdsBySelectedFloorNumbers, reservationRequestedResourceIds, selectedResourceId) =>
        selectedResourceId === null
            ? pipe(
                  excludedResourceIdsBySelectedFloorNumbers,
                  array.concat(reservationRequestedResourceIds),
                  array.uniq(string.Eq)
              )
            : reservationRequestedResourceIds
);

export const getFloorNumbersByResourceCategoryIds = createSelector(
    composeProps(getResources, createPropsExtender('enterpriseId', getEnterpriseIdFromServiceId)),
    getResourceIdsByResourceCategoryIds,
    (resources: Entities<Resource>, resourceIds) =>
        pipe(
            resourceIds,
            array.filterMap((id) => pipe(resources, record.lookup(id))),
            array.filterMap(({ data }) => option.fromNullable(data.value.floorNumber)),
            array.uniq(string.Eq),
            sortFloors
        )
);
